import { ControleToiListItem } from '../models/controle-toi.model';
import { ControleToiListaApiResponse } from '../models/toi-api.model';
import { ToiDetalhe } from '../models/toi-detalhe.model';
import {
  isToiStatusComContagemDias,
  normalizeToiStatusLabel,
  resolveStatusToiFromEventos,
} from './toi-status.util';

/** Campos alternativos que a API já devolveu para o mesmo dado ao longo das versões. */
type ControleToiListItemApi = ControleToiListItem &
  Record<string, unknown> & {
    nomeEmpresa?: string | null;
    idAtividadeToiStatus?: number | null;
    nrDiasPrazoRegularizacao?: number | null;
  };

/** GET /Lista pode responder um array puro ou um envelope paginado com nomes variados. */
export function extrairItensControleToi(
  response: ControleToiListaApiResponse
): ControleToiListItem[] {
  if (Array.isArray(response)) {
    return response;
  }

  const items =
    response?.itens ??
    response?.['items'] ??
    response?.['data'] ??
    response?.['dados'] ??
    response?.['result'] ??
    response?.['results'];

  return Array.isArray(items) ? (items as ControleToiListItem[]) : [];
}

export function extrairTotalRegistrosControleToi(
  response: ControleToiListaApiResponse,
  fallback: number
): number {
  if (Array.isArray(response)) {
    return response.length;
  }

  const total =
    response?.totalRegistros ??
    response?.['totalCount'] ??
    response?.['total'] ??
    response?.['totRows'] ??
    response?.['rowCount'];

  return typeof total === 'number' && Number.isFinite(total) ? total : fallback;
}

/** Normaliza o item da lista: status canônico, aliases de campos e prazo de regularização. */
export function mapControleToiListItem(item: ControleToiListItem): ControleToiListItem {
  const eventosToi = item.eventosToi ?? [];
  const itemApi = item as ControleToiListItemApi;

  const statusBruto =
    item.dsStatusToi?.trim() ||
    resolveStatusToiFromEventos(eventosToi, {
      flagEntregueMaos: item.flagEntregueMaos,
      flagRecusaRecebimento: item.flagRecusaRecebimento,
      flagGerado: item.flagGerado,
      flagExigeEnvioCorreios: item.flagExigeEnvioCorreios,
    });

  const dsStatusAtividade =
    item.dsStatusAtividade?.trim() ||
    item.status?.trim() ||
    item.dsStatusOs?.trim() ||
    null;

  return {
    ...item,
    idAtividadeToi: resolveIdAtividadeToiFromItem(item),
    nmEmpresa: item.nmEmpresa ?? itemApi.nomeEmpresa ?? null,
    dsTipoIrregularidade: item.dsTipoIrregularidade || item.dsTipoInfracao || '',
    dsStatusAtividade,
    eventosToi,
    statusToi: normalizeToiStatusLabel(statusBruto) ?? statusBruto,
    qtDiasPrazoRegularizacao:
      item.qtDiasPrazoRegularizacao ?? itemApi.nrDiasPrazoRegularizacao ?? null,
  };
}

function resolveIdAtividadeToiFromItem(item: ControleToiListItem): number | undefined {
  const record = item as ControleToiListItemApi;
  const candidates = [
    item.idAtividadeToi,
    record['IdAtividadeToi'],
    record['idHstAtividadeToi'],
    record['IdHstAtividadeToi'],
  ];

  for (const candidate of candidates) {
    const id = Number(candidate);
    if (Number.isFinite(id) && id > 0) {
      return id;
    }
  }

  return undefined;
}

type ItemContagemDias = Pick<
  ControleToiListItem,
  'statusToi' | 'dataEntregaMaos' | 'eventosToi' | 'qtDiasPrazoRegularizacao'
> & {
  dataEntregaCorreios?: string | null;
  DtEntregaCorreios?: string | null;
};

/** Data de referência para contagem de dias (entrega em mãos ou pelos Correios). */
export function resolveDataReferenciaEntregaToi(
  item: Omit<ItemContagemDias, 'qtDiasPrazoRegularizacao'>
): string | null {
  const status = normalizeToiStatusLabel(item.statusToi);
  if (status === 'Entregue em mãos') {
    return item.dataEntregaMaos?.trim() || null;
  }

  if (status === 'Entregue') {
    const dataCorreios = item.dataEntregaCorreios?.trim() || item.DtEntregaCorreios?.trim();
    if (dataCorreios) {
      return dataCorreios;
    }

    const eventoEntregue = item.eventosToi?.find(
      (evento) => normalizeToiStatusLabel(evento.status) === 'Entregue'
    );
    return eventoEntregue?.dataInicio?.trim() || null;
  }

  return null;
}

/** Dias corridos desde a entrega do TOI ao cliente (0 no dia da entrega). */
export function calcularDiasDesdeEntregaToi(
  item: ItemContagemDias,
  dataReferencia: Date = new Date()
): number | null {
  if (!isToiStatusComContagemDias(item.statusToi)) {
    return null;
  }

  const dataEntrega = resolveDataReferenciaEntregaToi(item);
  if (!dataEntrega) {
    return null;
  }

  const inicio = new Date(dataEntrega);
  if (Number.isNaN(inicio.getTime())) {
    return null;
  }

  const inicioDia = new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate());
  const referenciaDia = new Date(
    dataReferencia.getFullYear(),
    dataReferencia.getMonth(),
    dataReferencia.getDate()
  );
  const diffMs = referenciaDia.getTime() - inicioDia.getTime();

  return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
}

export function formatarDiasRegularizacaoToi(item: ItemContagemDias): string {
  const dias = calcularDiasDesdeEntregaToi(item);
  if (dias == null) {
    return '';
  }

  const prazo = item.qtDiasPrazoRegularizacao;
  if (typeof prazo === 'number' && Number.isFinite(prazo) && prazo > 0) {
    return `${dias} / ${prazo}`;
  }

  return String(dias);
}

/** Indica se o TOI exige correção de endereço antes do reenvio pelos Correios. */
export function exigeTratamentoEndereco(
  detalhe:
    | Pick<ToiDetalhe, 'flagEnderecoPendenteTratamento' | 'dsStatusToi' | 'eventosToi'>
    | null
    | undefined
): boolean {
  if (!detalhe) {
    return false;
  }

  if (detalhe.flagEnderecoPendenteTratamento) {
    return true;
  }

  const statusEvento = detalhe.eventosToi?.[0]?.status?.trim();
  const statusBruto = statusEvento || detalhe.dsStatusToi?.trim() || '';

  return (normalizeToiStatusLabel(statusBruto) ?? statusBruto) === 'Aguardando tratamento envio';
}
